import { injectAesthetic } from "./shared-styles.ts";
import * as yaml from "js-yaml";

/**
 * A lightweight YAML code editor component.
 * Uses a textarea with monospace styling and provides real-time validation.
 */
export class CodeEditor extends HTMLElement {
    private textarea: HTMLTextAreaElement | null = null;
    private lineNumbers: HTMLDivElement | null = null;
    private errorDisplay: HTMLDivElement | null = null;
    private _value: string = "";
    private seed: number;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.seed = Math.floor(Math.random() * 10000);
    }

    static get observedAttributes() {
        return ['value'];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name === 'value' && oldValue !== newValue) {
            // Only update if strictly different to avoid cursor jumping on trivial updates
            if (this._value !== newValue) {
                this._value = newValue;
                if (this.textarea) {
                    this.textarea.value = newValue;
                    this.updateLineNumbers();
                    this.validate();
                }
            }
        }
    }

    get value() {
        return this.textarea?.value || this._value;
    }

    set value(val: string) {
        if (this._value !== val) {
            this._value = val;
            if (this.textarea) {
                this.textarea.value = val;
                this.updateLineNumbers();
                this.validate();
            }
        }
    }

    connectedCallback() {
        this.render();
    }

    private validate() {
        if (!this.textarea || !this.errorDisplay) return;
        const val = this.textarea.value;
        try {
            if (val.trim()) {
                // @ts-ignore - js-yaml types from esm.sh sometimes miss exports
                yaml.load(val);
            }
            this.errorDisplay.textContent = "";
            this.errorDisplay.style.display = "none";
            this.textarea.classList.remove('invalid');
        } catch (e: any) {
            this.errorDisplay.textContent = `YAML Error: ${e.message}`;
            this.errorDisplay.style.display = "block";
            this.textarea.classList.add('invalid');
        }
    }

    private updateLineNumbers() {
        if (!this.textarea || !this.lineNumbers) return;
        const lines = this.textarea.value.split('\n').length;
        this.lineNumbers.innerHTML = Array.from({ length: Math.max(lines, 1) }, (_, i) => i + 1).join('<br>');
    }

    private handleScroll() {
        if (this.textarea && this.lineNumbers) {
            this.lineNumbers.scrollTop = this.textarea.scrollTop;
        }
    }

    private render() {
        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = `
            ${injectAesthetic(this.seed)}
            <style>
                :host {
                    display: block;
                    width: 100%;
                    height: 100%;
                    min-height: 400px;
                }
                .editor-wrapper {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    background: #0d1117;
                    overflow: hidden;
                    font-family: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace;
                    font-size: 0.9rem;
                    position: relative;
                }
                
                .editor-main {
                    display: flex;
                    flex: 1;
                    overflow: hidden;
                    position: relative;
                }

                .line-numbers {
                    padding: 1.5rem 0.75rem;
                    background: #161b22;
                    color: #484f58;
                    text-align: right;
                    user-select: none;
                    border-right: 1px solid #30363d;
                    min-width: 3rem;
                    line-height: 1.6;
                    font-size: 0.9rem;
                    overflow: hidden;
                }

                textarea {
                    flex: 1;
                    background: transparent;
                    color: #c9d1d9;
                    border: none;
                    padding: 1.5rem;
                    font-family: inherit;
                    font-size: inherit;
                    line-height: 1.6;
                    resize: none;
                    outline: none;
                    width: 100%;
                    white-space: pre;
                    overflow: auto;
                    tab-size: 2;
                }

                textarea.invalid {
                    background: rgba(239, 68, 68, 0.05);
                }

                .error-panel {
                    background: rgba(239, 68, 68, 0.1);
                    color: #fca5a5;
                    padding: 0.75rem 1.5rem;
                    font-size: 0.85rem;
                    font-family: monospace;
                    border-top: 1px solid rgba(239, 68, 68, 0.2);
                    display: none;
                    white-space: pre-wrap;
                }

                /* Scrollbar styling */
                textarea::-webkit-scrollbar {
                    width: 10px;
                    height: 10px;
                }
                textarea::-webkit-scrollbar-track {
                    background: #0d1117;
                }
                textarea::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 5px;
                    border: 2px solid #0d1117;
                }
                textarea::-webkit-scrollbar-thumb:hover {
                    background: rgba(255, 255, 255, 0.2);
                }
            </style>
            
            <div class="editor-wrapper sketch-base shadow-industrial">
                <div class="editor-main">
                    <div class="line-numbers"></div>
                    <textarea spellcheck="false" placeholder="Enter YAML configuration here..."></textarea>
                </div>
                <div class="error-panel"></div>
            </div>
        `;

        this.textarea = this.shadowRoot.querySelector('textarea');
        this.lineNumbers = this.shadowRoot.querySelector('.line-numbers');
        this.errorDisplay = this.shadowRoot.querySelector('.error-panel');

        if (this.textarea) {
            this.textarea.value = this._value;
            this.updateLineNumbers();

            this.textarea.addEventListener('input', () => {
                this.updateLineNumbers();
                this.validate();
                this.dispatchEvent(new CustomEvent('change', {
                    detail: { value: this.textarea?.value },
                    bubbles: true,
                    composed: true
                }));
            });

            this.textarea.addEventListener('scroll', () => this.handleScroll());

            this.textarea.addEventListener('keydown', (e) => {
                const ta = this.textarea!;
                if (e.key === 'Tab') {
                    e.preventDefault();
                    document.execCommand('insertText', false, '  ');
                }
            });

            this.validate();
        }
    }
}

customElements.define('code-editor', CodeEditor);
